<script setup>
import { computed, onMounted, ref, defineEmits } from "vue"

import Lottie from "@/components/widgets/lottie.vue"
import animationData from "@/components/widgets/msoeawqm.json"
import PersianDate from "persian-date"

import store from "../../state/store"
import useTags from "../../use/useTags"
import useDebouncedFunction from "../../use/useDebouncedFunction"
import TableHeaderCell from "@/components/table/TableHeaderCell.vue"

const tags = computed(() => store.state.tags)

const { search, perPage, sortField, sortDirection, getTags, sortTags } =
  useTags()
const { handleInput } = useDebouncedFunction(getTags)

const emit = defineEmits(["clickEdit"])

onMounted(() => {
  getTags()
})

const toPersianDateTime = (date) => {
  const [datePart, timePart] = date.split(" ")
  const [year, month, day] = datePart.split("-")
  const [hour, minute, second] = timePart.split(":")
  const gregorianDate = new Date(year, month - 1, day, hour, minute, second)
  const persianDate = new PersianDate(gregorianDate)
  return persianDate.format("YYYY/MM/DD HH:mm:ss")
}

function getForPage(ev, link) {
  ev.preventDefault()
  if (!link.url || link.active) {
    return
  }
  getTags(link.url)
}

const defaultOptions = ref({
  animationData: animationData,
})

const editTag = (tag) => {
  emit("clickEdit", tag)
}
</script>

<template>
  <b-card-body class="border-bottom-dashed border-bottom">
    <b-row class="g-3">
      <b-col xl="9">
        <div class="search-box">
          <input
            v-model="search"
            @input="handleInput"
            type="text"
            class="form-control search"
            placeholder="جستجوی عنوان برچسب ..."
          />
          <i class="ri-search-line search-icon"></i>
        </div>
      </b-col>

      <b-col xl="3">
        <b-row class="g-3">
          <b-col sm="12">
            <select
              @change="getTags(null)"
              v-model="perPage"
              class="form-select mb-3"
              aria-label="Default select example"
            >
              <option value="" selected>نمایش تعداد ردیف</option>
              <option value="2">2</option>
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="50">50</option>
            </select>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </b-card-body>
  <b-card-body>
    <div>
      <div class="table-responsive table-card mb-1">
        <table class="table align-middle" id="tagTable">
          <thead class="table-light text-muted">
            <tr>
              <th class="sort" data-sort="tag_image">تصویر</th>
              <TableHeaderCell
                field="tag_name"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortTags('tag_name')"
              >
                عنوان برچسب
              </TableHeaderCell>

              <TableHeaderCell
                field="tag_priority"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortTags('tag_priority')"
              >
                اولویت
              </TableHeaderCell>

              <TableHeaderCell
                field="created_at"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortTags('created_at')"
              >
                تاریخ ثبت
              </TableHeaderCell>
              <th class="sort" data-sort="action">عملیات</th>
            </tr>
          </thead>

          <tbody class="list" v-if="tags.loading || !tags.data.length">
            <tr>
              <td colspan="8">
                <div class="text-center">
                  <b-spinner
                    v-if="tags.loading"
                    variant="dark"
                    style="width: 3rem; height: 3rem"
                  ></b-spinner>
                  <div v-else>
                    <lottie
                      class="avatar-xl"
                      colors="primary:#121331,secondary:#08a88a"
                      :options="defaultOptions"
                      :height="75"
                      :width="75"
                    />
                    <h5 class="mt-2">هیچ برچسبی وجود ندارد!</h5>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>

          <tbody class="list" v-else>
            <tr v-for="(tag, index) of tags.data" :key="index">
              <td class="tag_image">
                <div class="avatar-sm bg-light rounded p-1">
                  <img
                    :src="tag.image"
                    alt="tag.title"
                    class="img-fluid d-block"
                  />
                </div>
              </td>
              <td class="tag_name">{{ tag.title }}</td>
              <td class="tag_priority">{{ tag.tag_priority }}</td>
              <td class="date">{{ toPersianDateTime(tag.created_at) }}</td>
              <td>
                <ul class="list-inline hstack gap-2 mb-0">
                  <li
                    class="list-inline-item edit"
                    data-bs-toggle="tooltip"
                    data-bs-trigger="hover"
                    data-bs-placement="top"
                    title="Edit"
                  >
                    <b-link
                      variant="text-primary"
                      class="d-inline-block edit-item-btn"
                      @click="editTag(tag)"
                    >
                      <i class="ri-pencil-fill fs-16"></i>
                    </b-link>
                  </li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="d-flex justify-content-end" v-if="tags.total > tags.limit">
        <div class="pagination-wrap hstack gap-2" style="display: flex">
          <ul class="pagination listjs-pagination mb-0">
            <li
              v-for="(link, i) of tags.links"
              :key="i"
              class="page-item"
              :class="{
                disabled: !link.url,
                active: link.active || !link.url,
              }"
            >
              <span
                class="page-link"
                v-if="link.active"
                v-html="link.label"
              ></span>

              <a
                v-else
                href="#"
                @click="getForPage($event, link)"
                class="page-link"
                v-html="link.label"
              >
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </b-card-body>
</template>
